"use client";

import { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import {
  IconJewishStarFilled,
  IconUser,
} from "@/lib/icons";
import {
  Avatar,
  Card,
  Modal,
} from "antd";
import { MessageOutlined } from "@ant-design/icons";
import SliderNavButtons from "@/components/common/SliderNavButtons";
import styles from "./reviews.module.css";
import "./gallery.css";

function Reviews({ locationData, title }) {
  const [swiperInstance, setSwiperInstance] = useState(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const [selectedReview, setSelectedReview] = useState(null);
  const reviews = locationData?.reviews || [];

  const formatDate = (unixTime) => {
    if (!unixTime) return "—";
    return new Date(unixTime * 1000).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getInitials = (name = "") =>
    name
      .split(" ")
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  return (
    <div className={styles.reviewsSection}>
      {/* Header */}
      <div
        className={`flex ${title ? "justify-between" : "justify-end"} items-center mb-6`}
      >
        {title && (
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold bricolage text-[#021526]">
            {title}
          </h2>
        )}

        {reviews.length > 1 && (
          <div className="hidden md:flex">
            <SliderNavButtons
              prevRef={prevRef}
              nextRef={nextRef}
              swiper={swiperInstance}
              itemsCount={reviews.length}
            />
          </div>
        )}
      </div>

      {/* Empty state */}
      {reviews.length === 0 && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <MessageOutlined className="text-4xl text-gray-300" />
          <p className="mt-3 text-gray-600 text-sm font-medium">
            No reviews yet
          </p>
        </div>
      )}

      {/* Reviews Swiper */}
      {reviews.length > 0 && (
        <div className="relative w-full">
          <Swiper
            modules={[Navigation, Pagination]}
            onSwiper={setSwiperInstance}
            onInit={(swiper) => {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
              swiper.navigation.init();
              swiper.navigation.update();
            }}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            pagination={{
              clickable: true,
            }}
            onSlideChange={(swiper) => {
              swiper.pagination?.update();
            }}
            slidesPerView={1.12}
            spaceBetween={16}
            breakpoints={{
              480: {
                slidesPerView: 1.25,
                spaceBetween: 16,
              },
              576: {
                slidesPerView: 2,
                spaceBetween: 16,
              },
              768: {
                slidesPerView: 2.5,
                spaceBetween: 18,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 20,
              },
              1280: {
                slidesPerView: 4,
                spaceBetween: 24,
              },
            }}
            className={styles.reviewsSwiper}
          >
            {reviews.map((review, index) => (
              <SwiperSlide key={index} className="!h-auto flex">
                <Card
                  onClick={() => setSelectedReview(review)}
                  className="rounded-2xl border-gray-200 h-full w-full hover:shadow-lg cursor-pointer transition-shadow"
                  styles={{
                    body: {
                      padding: 20,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      height: "100%",
                    },
                  }}
                >
                  <div>
                    {/* Top */}
                    <div className="flex flex-col items-start gap-4 mb-4">
                      <Avatar
                        size={44}
                        src={review?.profile_photo_url}
                        alt={review?.author_name || "Reviewer"}
                        icon={<IconUser />}
                      >
                        {!review?.profile_photo_url &&
                          getInitials(review?.author_name)}
                      </Avatar>

                      <p className="text-xl sm:text-2xl font-extrabold bricolage text-[#021526] line-clamp-1">
                        {review?.author_name || "Anonymous User"}
                      </p>
                    </div>

                    {/* Review Text */}
                    <p className="text-gray-600 text-sm font-normal leading-[155%] line-clamp-5">
                      {review?.text}
                    </p>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between mt-6 pt-2 border-t border-gray-100">
                    <span className="text-[#021526] font-medium text-sm">
                      {formatDate(review?.time)}
                    </span>

                    {review?.rating && (
                      <div className="flex gap-1 items-center">
                        <IconJewishStarFilled
                          size={16}
                          className="text-[#FFD980]"
                        />
                        <span className="text-gray-700 font-medium text-sm">
                          {review.rating} / 5
                        </span>
                      </div>
                    )}
                  </div>
                </Card>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {/* Review Modal */}
      <Modal
        open={!!selectedReview}
        onCancel={() => setSelectedReview(null)}
        footer={null}
        centered
        width={600}
      >
        {selectedReview && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
              <Avatar
                size={56}
                src={selectedReview?.profile_photo_url}
                alt={selectedReview?.author_name || "Reviewer"}
                icon={<IconUser />}
              >
                {!selectedReview?.profile_photo_url &&
                  getInitials(selectedReview?.author_name)}
              </Avatar>

              <div>
                <p className="text-lg font-bold text-[#021526]">
                  {selectedReview?.author_name || "Anonymous User"}
                </p>
                <p className="text-sm text-gray-500">
                  {formatDate(selectedReview?.time)}
                </p>
              </div>
            </div>

            {/* Rating */}
            {selectedReview?.rating && (
              <div className="flex items-center gap-2">
                <IconJewishStarFilled size={18} className="text-[#FFD980]" />
                <span className="font-medium text-gray-700">
                  {selectedReview.rating} / 5
                </span>
              </div>
            )}

            {/* Full Review Text */}
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {selectedReview?.text}
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Reviews;
